// 機關時間軸：排在 3D 畫面下面（右邊欄太窄，時間軸要寬）。畫法像 Cinema 4D 的 Timeline，三個地方共用：
//   導演頁歌頁（pages/Preview.tsx）、編輯頁（pages/ProjectEditor.tsx）、Cue 庫頁（single）
//
// 畫面：
//   時間尺 → （可以改時）範圍條那一列 → 總覽列（cue 編號 + GO→到位的轉場條 + 所有 key 彙總的小 key）→ 每個機關一條軌道
//   每一段動作 = 開始 key（方形）+ 結束 key（方形）+ 中間一條實心帶；0 秒的菱形是起始 key（有起始位置才有）
//   未選 = 空心；選到 = 橘色實心 + 加粗外框
//   重疊照實畫（算法在 motion.ts，這裡不改）：被接手 → 帶子畫到接手那一秒、結束 key 半實心；被取消 → 淡色空心；轉場被擋短 → 淡色虛線畫出設定的長度
//
// props：
//   mech            時間軸資料（TimelineMech：timeline、mechanisms、groups、focusedId…）
//   cues            這首歌的 cue（正式 + 提案；只畫正式的。要傳原本的 cue，不是填過暫排時間的）
//   selectedCueId / onSelectCue / onClose
//   single          Cue 庫裡看一個 cue：cues 是 [預設位置, 這個 cue]（lib/mechanisms/libraryView.ts）
//   readOnly        只能看（審片、動畫師、分享頁）：不能選、不能拖；給了 edit 就以 edit 為準
//   clock           播放頭跟著這個時間走（影片 / 音樂）；不給 = 時間軸自己播
//   edit            可以改的介面（lib/mechanisms/timelineEdit.ts）：選取、框選、拖 key / 帶 / 範圍條、cue 編號、轉場條、歌曲長度
//   startLabel      起始位置的來源（例：「接上一首：S02」）：寫在標題列和起始 key 的提示
//   readOnlyHint    只能看時標題列的字；不給 = 預設那句；null = 不顯示
//   lengthSec       只能看模式的歌曲長度（秒）；有 edit.length 時以 edit.length.seconds 為準
//   heightKey       高度各自記住的名字（'preview' / 'editor' / 'cues'）；預設 'default'
//   startControl    （可以改時）標題列上的起始位置：短標籤，點了才展開選項（Cue 庫頁）；不給 = 只寫 startLabel
//   showWhenEmpty   編輯頁：還沒有機關 / 還沒有 cue 時間也畫時間尺（沒有的就空著）；不給 = 維持「沒機關 = 一行說明文字」
//   collapsed       收起來：只留一條細的（時間尺 + cue 編號 + 展開鈕 + 播放），還是能拉、能點、能跳 cue；onOpen = 展開
//
// 三種模式：
//   有 edit       可選取、框選、整批拖；放開交給 edit 的回呼（草稿 / 後台由頁面管）；只有 editableCueId 那個 cue 拖得動
//   只有 readOnly 只能看
//   都沒給        編輯頁舊路徑：拖開始 key / 帶改延遲、拖結束 key 改走幾秒，放開用 mech.commitTiming 即時存（沒有框選）
//
// 每幀不重畫 React：播放頭、時間字直接改 DOM；機關位置 StageScene 每幀問 mech.previewTimeRef
// 算術都在 lib/adjust/timelineKeys.ts（有測試），高度在 lib/adjust/timelineHeight.ts
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, ChevronUp, CircleHelp, Lock, Pause, Play, SlidersHorizontal, Square, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import Icon from '../Icon';
import { toast } from '../../lib/toast';
import './mechanisms.css';
import type { Cue } from '../../lib/api';
import { cueTransitionSeconds } from '../../lib/cueTiming';
import type { Mechanism, MechanismGroup } from '../../lib/mechanisms/model';
import { layoutMechanisms } from '../../lib/mechanisms/group';
import { MechanismTimeline as TimelineData, formatMechValue, formatSeconds, sortMasters, type TimelineMove } from '../../lib/mechanisms/motion';
import { sheetTime } from '../../lib/mechanisms/cueSheet';
import { LIB_START_ID } from '../../lib/mechanisms/libraryView';
import { LENGTH_TEXT, formatLength, totalLength } from '../../lib/mechanisms/songLength';
import { STAGE_TIMELINE_TEXT, editHandlers, timelineEditing, type TimelineClock, type TimelineMech } from '../../lib/mechanisms/stageTimeline';
import type { TimelineEdit } from '../../lib/mechanisms/timelineEdit';
import { useTimelineHeight } from '../../lib/adjust/timelineHeight';
import { useMusicHotkeys } from '../../lib/musicClock';
import {
  TIMELINE_TEXT, batchOf, changesFrom, combineSelection, carryCueMove, cueMoveResult, cutStateOf, dragScale, dragShift, edgeScrollSpeed, keyId, keyIdsOfMove, keysInBox,
  lengthFromDrag, moveId, overlapText, pickCalls, prevArriveOf, scaleBatch, selectionRange, shiftBatch, snapTime, summaryKeys,
  transitionFromDrag, transitionMovePreview, transitionScale, transitionText,
  type BatchMode, type BatchMove, type KeyEnd, type MarqueeHow, type PlacedKey, type TimingMap,
} from '../../lib/adjust/timelineKeys';

interface Props {
  mech: TimelineMech;
  cues: Cue[];
  selectedCueId: string | null;
  onSelectCue: (id: string) => void;
  onClose: () => void;
  single?: { label: string; name: string } | null;
  readOnly?: boolean;
  clock?: TimelineClock | null;
  edit?: TimelineEdit | null;
  startLabel?: string | null;
  readOnlyHint?: string | null;
  lengthSec?: number | null;
  heightKey?: string;
  startControl?: ReactNode;
  /** 只能看、但這個人有調整權限（審片模式的導演、製作）：標題列放一顆「調整舞台」，按了等於按上方切換鈕 */
  onAdjust?: (() => void) | null;
  /** 空白鍵 = 播放 / 暫停（只在時間軸自己播、沒有影片 / 音樂跟著走時才用；有影片 / 音樂的空白鍵由頁面上別的地方管，不能重複） */
  spaceHotkey?: boolean;
  /** 這個數字變了 = 外面要結束預覽（去改值、換 cue）：停止自己播、播放頭收起來 */
  stopSignal?: number;
  showWhenEmpty?: boolean;
  collapsed?: boolean;
  onOpen?: (() => void) | null;
}

const LABEL_W = 132;
const ROW_H = 28;
const RULER_H = 30, RANGE_H = 16, OVERVIEW_H = 34;
const PAD = 8;
const MIN_PPS = 2, MAX_PPS = 240;
const clamp0 = (v: number) => Math.max(0, Math.min(3600, v));
const EMPTY_SEL: ReadonlySet<string> = new Set<string>();
const MOVE_THRESHOLD = 3;
/** 收起來的細條：時間尺 + 總覽列（cue 編號）+ 上下各一條線 */
const C_RULER_H = 22, C_OVERVIEW_H = 30;
export const COLLAPSED_H = C_RULER_H + C_OVERVIEW_H + 2;
/** 沒有任何機關 / cue 時間時用的空時間軸（只有時間尺） */
const EMPTY_TL = new TimelineData([], [], [], null);

/** 拖到一半的狀態：預覽照這個畫，放開才交出去 */
type Gesture =
  | { kind: 'keys'; x0: number; items: BatchMove[]; mode: BatchMode; anchor: number; part: KeyEnd | 'band' | 'range'; delta: number; atGo: boolean; moved: boolean; clickSel: ReadonlySet<string> | null; clickReplace?: boolean; pending?: false;
      /** 按在別的 cue 上：等頁面切到那個 cue（資料好了）才開始拖；wait = 要等的 cue */
      wait?: string; want?: ReadonlySet<string>; wantReplace?: boolean; anchorMech?: string }
  | { kind: 'scale'; x0: number; items: BatchMove[]; range: { min: number; max: number }; side: 'left' | 'right'; k: number; anchorT: number; edge: number; moved: boolean; pending?: false }
  | { kind: 'cue'; x0: number; cueId: string; ids: ReadonlySet<string>; delta: number; moved: boolean; pending: boolean; tl: unknown }
  | { kind: 'transition'; x0: number; cueId: string; arrive: number; go0: number; prevArrive: number | null; oldSec: number; seconds: number; moved: boolean; pending: boolean; tl: unknown }
  | { kind: 'length'; x0: number; len0: number; total0: number; seconds: number; moved: boolean; pending: boolean }
  | { kind: 'marquee'; cx0: number; cy0: number; ax: number; ay: number; x: number; y: number; how: MarqueeHow; moved: boolean; pending?: false };

/** 鍵盤微調色塊：先改草稿，放開再存（像滑桿） */
type Nudge = { cueId: string; mechId: string; delay0: number; dur0: number; delay: number; dur: number };

/** 時間軸上的一行：一個機關，或一排的標題行（收起來時畫整排合併的色塊） */
type TlRow =
  | { key: string; kind: 'mech'; mech: Mechanism; member: boolean }
  | { key: string; kind: 'group'; group: MechanismGroup; members: Mechanism[]; collapsed: boolean };

const capture = (e: React.PointerEvent) => { try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* 已經放開 */ } };

export default function MechanismTimeline({
  mech, cues, selectedCueId, onSelectCue, onClose, single = null, readOnly = false, clock = null,
  edit = null, startLabel = null, readOnlyHint, lengthSec = null, heightKey = 'default', startControl = null, onAdjust = null, spaceHotkey = false, stopSignal = 0,
  showWhenEmpty = false, collapsed: strip = false, onOpen = null,
}: Props) {
  const tl = mech.timeline ?? (showWhenEmpty ? EMPTY_TL : null);
  const mechanisms = mech.mechanisms;
  const viewOnly = !edit && readOnly;
  const legacy = !edit && !readOnly;
  const can = timelineEditing(viewOnly);
  const timeClock = clock;
  const timeClockRef = useRef(timeClock);
  timeClockRef.current = timeClock;
  const masters = useMemo(() => sortMasters(cues), [cues]);
  const cueNo = useMemo(() => new Map<string, string | number>(masters.map((c, i) => [c.id, single ? single.label : i + 1])), [masters, single]);
  const cueById = useMemo(() => new Map(cues.map(c => [c.id, c])), [cues]);
  // 暫排的 cue = 正式 cue 裡沒設影片時間的（時間軸給的位置是 lib/provisionalCues.ts 排的）
  const provisionalIds = useMemo(
    () => new Set(single ? [] : masters.filter(c => c.videoTimeSec == null).map(c => c.id)),
    [masters, single],
  );
  const untimed = provisionalIds.size;

  const sectionRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const rulerHeadRef = useRef<HTMLDivElement>(null);
  const rulerWrapRef = useRef<HTMLDivElement>(null);
  // 時間尺固定在最上面（上下捲只捲機關那幾排），左右捲時跟著色塊一起動
  const hbarRef = useRef<HTMLDivElement>(null);
  const syncRuler = () => {
    const sc = scrollRef.current;
    if (!sc) return;
    if (rulerWrapRef.current) rulerWrapRef.current.scrollLeft = sc.scrollLeft;
    if (hbarRef.current) hbarRef.current.scrollLeft = sc.scrollLeft;
  };
  const clockRef = useRef<HTMLSpanElement>(null);
  const { height, handleProps: heightHandle } = useTimelineHeight(heightKey, sectionRef);

  const [pps, setPps] = useState<number | null>(null);   // 每秒幾 px；null = 還沒量（整首放得下）
  const [playing, setPlaying] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [gesture, setGestureState] = useState<Gesture | null>(null);
  const gestureRef = useRef<Gesture | null>(null);
  gestureRef.current = gesture;
  const setGesture = (g: Gesture | null) => { gestureRef.current = g; setGestureState(g); };
  const [nudge, setNudgeState] = useState<Nudge | null>(null);
  const nudgeRef = useRef<Nudge | null>(null);
  nudgeRef.current = nudge;
  const setNudge = (n: Nudge | null) => { nudgeRef.current = n; setNudgeState(n); };
  // 選到的 key（時間軸自己的；所屬機關同步回報 edit.onPick）。cue 編號的多選是另外一份，兩邊互不影響
  const [sel, setSelState] = useState<ReadonlySet<string>>(EMPTY_SEL);
  const selRef = useRef(sel);
  selRef.current = sel;
  const [cuePicked, setCuePicked] = useState<ReadonlySet<string>>(EMPTY_SEL);
  const lastPtr = useRef<{ x: number; y: number; alt: boolean } | null>(null);
  const suppressCueClick = useRef(false);

  // 一排機關：標題行可以收起來（預設展開）；一排的成員接在標題行後面
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const rows = useMemo<TlRow[]>(() => {
    const out: TlRow[] = [];
    for (const it of layoutMechanisms(mechanisms, mech.groups)) {
      if (it.type === 'single') { out.push({ key: it.mech.id, kind: 'mech', mech: it.mech, member: false }); continue; }
      if (it.type === 'rig') {
        // 吊掛：整組是一個可以收起來的標題行，每個吊點是它的成員
        const gid = 'rig:' + it.rig.id;
        const isCollapsed = collapsed.has(gid);
        out.push({
          key: 'g:' + gid, kind: 'group', group: { id: gid, name: it.rig.name, order: it.rig.order, settings: null, updatedAt: '' },
          members: it.hoists, collapsed: isCollapsed,
        });
        if (!isCollapsed) for (const m of it.hoists) out.push({ key: m.id, kind: 'mech', mech: m, member: true });
        continue;
      }
      const isCollapsed = collapsed.has(it.group.id);
      out.push({ key: 'g:' + it.group.id, kind: 'group', group: it.group, members: it.members, collapsed: isCollapsed });
      if (!isCollapsed) for (const m of it.members) out.push({ key: m.id, kind: 'mech', mech: m, member: true });
    }
    return out;
  }, [mechanisms, mech.groups, collapsed]);

  const moves = tl?.moves ?? [];
  const movesBy = useMemo(() => {
    const out = new Map<string, TimelineMove[]>();
    for (const mv of tl?.moves ?? []) {
      const list = out.get(mv.mechId) || [];
      list.push(mv);
      out.set(mv.mechId, list);
    }
    return out;
  }, [tl]);
  const pickOrder = useMemo(() => {
    const out: string[] = [];
    for (const r of rows) { if (r.kind === 'mech') out.push(r.mech.id); else if (r.collapsed) out.push(...r.members.map(m => m.id)); }
    return out;
  }, [rows]);
  // 所有 key 的資料座標（時間 + 第幾行）：框選、全選都算這份，捲出畫面的也在裡面；收起來的一排算在標題行
  const placed = useMemo(() => {
    const out: PlacedKey[] = [];
    rows.forEach((row, i) => {
      const ids = row.kind === 'mech' ? [row.mech.id] : row.collapsed ? row.members.map(m => m.id) : [];
      for (const id of ids) {
        for (const mv of movesBy.get(id) || []) {
          out.push({ cueId: mv.cueId, mechId: mv.mechId, end: 'begin', time: mv.start, row: i });
          out.push({ cueId: mv.cueId, mechId: mv.mechId, end: 'end', time: cutStateOf(mv) === 'cut' ? mv.cut!.at : mv.end, row: i });
        }
      }
    });
    return out;
  }, [rows, movesBy]);

  // 頁面（3D、面板）把某個機關取消選取 → 它的 key 也跟著取消，不然時間軸和面板對不上
  const pickedKey = edit ? edit.pickedMechIds.join('\u0000') : '';
  const picked = useMemo(() => new Set(edit?.pickedMechIds ?? []), [pickedKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!edit || selRef.current.size === 0) return;
    const next = new Set<string>();
    for (const k of placed) { const id = keyId(k); if (selRef.current.has(id) && picked.has(k.mechId)) next.add(id); }
    if (next.size !== selRef.current.size) { selRef.current = next; setSelState(next); }
  }, [pickedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 長度 ──
  const visibleWindows = (tl?.windows ?? []).filter(w => w.cueId !== LIB_START_ID);
  const lastCueTime = visibleWindows.length ? visibleWindows[visibleWindows.length - 1].arrive : null;
  const baseTotal = totalLength({
    endTime: tl?.endTime ?? 0, single: !!single,
    lengthSec: edit?.length?.seconds ?? lengthSec, clockDuration: timeClock?.duration, lastCueTime,
  });
  const lenG = gesture?.kind === 'length' ? gesture : null;
  const total = lenG ? Math.max(lenG.total0, lenG.seconds) : baseTotal;

  // 量寬度 / 算時間用的那一塊：展開 = 捲動區；收起來沒有捲動區，用時間尺那一條（一樣寬）
  const viewEl = () => scrollRef.current ?? rulerWrapRef.current;
  const fit = () => {
    const w = viewEl()?.clientWidth || 600;
    setPps(Math.max(MIN_PPS, Math.min(MAX_PPS, (w - PAD * 2) / total)));
  };
  useEffect(() => { if (pps === null && tl) fit(); }); // eslint-disable-line react-hooks/exhaustive-deps
  // 展開 / 收起來都整首放進來；收起來時條子的寬度變了（拖側邊欄、換視窗大小）也跟著重新放
  const firstCollapse = useRef(true);
  useEffect(() => {
    if (firstCollapse.current) { firstCollapse.current = false; return; }
    setPps(null);
  }, [strip]);
  useEffect(() => {
    if (!strip || typeof ResizeObserver === 'undefined') return;
    const el = rulerWrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(el);
    return () => ro.disconnect();
  }, [strip, total]); // eslint-disable-line react-hooks/exhaustive-deps
  const scale = pps ?? 10;
  const width = Math.ceil(total * scale) + PAD * 2;
  const X = (t: number) => PAD + t * scale;
  // 播放 / 暫停：播放鈕和空白鍵共用（跟著影片 / 音樂時交給它們的播放器；自己播時從頭或接著播）
  const togglePlay = () => {
    if (timeClock) { timeClock.toggle(); return; }
    if (playing) setPlaying(false); else { if (mech.previewTimeRef.current === null || mech.previewTimeRef.current >= total) setTime(0); setPreviewing(true); setPlaying(true); }
  };
  useMusicHotkeys({ enabled: spaceHotkey && !timeClock && !!tl, onToggle: togglePlay });

  // Ctrl + 滾輪 = 放大縮小（要自己掛，React 的 wheel 不能擋預設）
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setPps(p => Math.max(MIN_PPS, Math.min(MAX_PPS, (p ?? 10) * (e.deltaY < 0 ? 1.25 : 0.8))));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [strip]);

  // ── 播放頭（只改 DOM）──
  const showHead = (t: number | null) => {
    for (const h of [headRef.current, rulerHeadRef.current]) {
      if (h) { h.style.display = t === null ? 'none' : 'block'; if (t !== null) h.style.transform = `translateX(${t * scale}px)`; }
    }
    if (clockRef.current) clockRef.current.textContent = t === null ? '' : sheetTime(t);
  };
  const firstStop = useRef(true);
  useEffect(() => {
    if (firstStop.current) { firstStop.current = false; return; }
    setPlaying(false);
    if (!timeClockRef.current) setTime(null);
  }, [stopSignal]); // eslint-disable-line react-hooks/exhaustive-deps
  const setTime = (t: number | null) => {
    mech.setPreviewTime(t);
    showHead(t);
    setPreviewing(t !== null);
  };
  useEffect(() => { showHead(timeClockRef.current ? timeClockRef.current.now() : mech.previewTimeRef.current); syncRuler(); }); // 縮放後播放頭跟著
  // 跟著別人的時間（導演頁的影片）：播放頭每幀讀它的時間，只改 DOM
  useEffect(() => {
    if (!timeClock) return;
    let raf = 0;
    const step = () => {
      const c = timeClockRef.current;
      showHead(c ? c.now() : null);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [!!timeClock, scale]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { mech.setPreviewTime(null); }, [mech.setPreviewTime]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    let last = performance.now();
    const step = (now: number) => {
      const t = (mech.previewTimeRef.current ?? 0) + (now - last) / 1000;
      last = now;
      if (t >= total) { mech.setPreviewTime(total); showHead(total); setPlaying(false); return; }
      mech.setPreviewTime(t);
      showHead(t);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [playing, total, scale]); // eslint-disable-line react-hooks/exhaustive-deps

  const timeAtX = (clientX: number) => {
    const el = viewEl()!;
    const r = el.getBoundingClientRect();
    return Math.max(0, Math.min(total, (clientX - r.left + el.scrollLeft - PAD) / scale));
  };
  // 點 / 拖時間尺：跟著影片時 → 讓影片跳過去（3D 本來就跟著影片）；自己播時 → 3D 的機關跟著這個時間
  const scrub = (t: number) => {
    const c = timeClockRef.current;
    if (c) { c.seek(t); showHead(t); } else setTime(t);
  };
  const onRulerDown = (e: React.PointerEvent) => {
    capture(e);
    if (!timeClockRef.current) setPlaying(false);
    scrub(timeAtX(e.clientX));
  };
  const onRulerMove = (e: React.PointerEvent) => {
    if (e.buttons & 1) scrub(timeAtX(e.clientX));
  };

  // ── 選取 ──
  const focusSelf = () => { sectionRef.current?.focus?.({ preventScroll: true }); };
  const applySel = (next: ReadonlySet<string>, replace: boolean) => {
    const before = selRef.current;
    selRef.current = next;
    setSelState(next);
    if (!edit) return;
    for (const c of pickCalls(before, next, replace)) edit.onPick(c.ids, c.how, pickOrder);
  };
  const cueEditable = (cueId: string) => (edit ? edit.editableCueId === cueId : legacy);
  /** 按住能拖：選中的 cue 直接拖；別的 cue 也能（先切過去，同一次按住接著拖） */
  const grabbable = (cueId: string) => !viewOnly && (edit ? cueId !== LIB_START_ID : legacy);
  const select = (mv: TimelineMove) => {
    if (mv.cueId !== selectedCueId) onSelectCue(mv.cueId);
    if (mech.focusedId !== mv.mechId) mech.setFocusedId(mv.mechId);
    if (playing) setPlaying(false);
  };

  // ── 拖 key / 帶（舊路徑也走這裡，只是一次一段、不選取）──
  /** 按下 key / 帶：ids = 這一下碰到的 key；anchorMv = 被抓住的那段 */
  const pressKeys = (e: React.PointerEvent, ids: string[], anchorMv: TimelineMove, part: KeyEnd | 'band') => {
    if (viewOnly || e.button > 0) return;
    e.stopPropagation();
    focusSelf();
    if (legacy) {
      capture(e);
      select(anchorMv);
      const items = [{ cueId: anchorMv.cueId, mechId: anchorMv.mechId, go: anchorMv.go, delay: anchorMv.delay, duration: anchorMv.duration, begin: part !== 'end', end: part === 'end' }];
      setGesture({ kind: 'keys', x0: e.clientX, items, mode: 'all', anchor: part === 'end' ? anchorMv.duration : anchorMv.delay, part, delta: 0, atGo: false, moved: false, clickSel: null });
      return;
    }
    if (playing) setPlaying(false);
    const cur = selRef.current;
    const ctrl = e.ctrlKey || e.metaKey;
    const already = ids.every(id => cur.has(id));
    let now: ReadonlySet<string> = cur;          // 按下就生效的選取
    let later: ReadonlySet<string> | null = null; // 沒拖、只是點一下才生效（點已選到的 key：可能是要拖整批）
    let replace = false, laterReplace = false;
    if (ctrl) {
      if (already) { const n = new Set(cur); for (const id of ids) n.delete(id); later = n; }
      else now = combineSelection(cur, ids, 'add');
    } else if (e.shiftKey) {
      now = combineSelection(cur, ids, 'add');
    } else if (!already) {
      now = new Set(ids);
      replace = true;
    } else {
      later = new Set(ids);
      laterReplace = true;
    }
    if (!edit || !cueEditable(anchorMv.cueId)) {
      // 別的 cue：按住直接拖 → 先請頁面切到那個 cue（有沒存的草稿會先問），資料好了同一次按住接著拖；Ctrl 點已選到的 = 取消選取，不拖
      if (edit && anchorMv.cueId !== LIB_START_ID && !(ctrl && already)) {
        capture(e);
        edit?.onGestureStart?.();
        if (now !== cur) applySel(now, replace);
        if (anchorMv.cueId !== selectedCueId) onSelectCue(anchorMv.cueId);
        setGesture({
          kind: 'keys', x0: e.clientX, items: [], mode: part === 'end' ? 'ends' : 'all', anchor: 0, part, delta: 0, atGo: false, moved: false, clickSel: null,
          wait: anchorMv.cueId, want: now, wantReplace: replace, anchorMech: anchorMv.mechId,
        });
        return;
      }
      if (later) applySel(later, laterReplace);
      else if (now !== cur) applySel(now, replace);
      return;
    }
    if (now !== cur) applySel(now, replace);
    capture(e);
    edit.onGestureStart?.();
    const items = batchOf(moves, now, edit.editableCueId);
    const mode: BatchMode = part === 'end' ? 'ends' : 'all';
    const anchor = part === 'end' ? anchorMv.duration : (items.find(i => i.mechId === anchorMv.mechId && i.begin) ? anchorMv.delay : anchorMv.duration);
    setGesture({ kind: 'keys', x0: e.clientX, items, mode, anchor, part, delta: 0, atGo: false, moved: false, clickSel: later, clickReplace: laterReplace });
  };

  // 按在別的 cue 上等切換：頁面切好、那個 cue 的資料好了（editableCueId 變成它）→ 選取接上、開始拖
  useEffect(() => {
    const g = gestureRef.current;
    if (!edit || !g || g.kind !== 'keys' || !g.wait || edit.editableCueId !== g.wait) return;
    const want = g.want ?? EMPTY_SEL;
    applySel(want, !!g.wantReplace);
    const items = batchOf(moves, want, edit.editableCueId);
    const am = items.find(i => i.mechId === g.anchorMech);
    if (!am) { setGesture(null); return; }
    const anchor = g.part === 'end' ? am.duration : (am.begin ? am.delay : am.duration);
    setGesture({ ...g, wait: undefined, want: undefined, items, anchor });
  }, [edit?.editableCueId, tl]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 範圍條 ──
  const pressRange = (e: React.PointerEvent, side: 'left' | 'right' | null) => {
    if (!edit || e.button > 0) return;
    e.stopPropagation();
    focusSelf();
    const items = batchOf(moves, selRef.current, edit.editableCueId);
    const range = selectionRange(items);
    if (!range) return;
    capture(e);
    edit.onGestureStart?.();
    if (playing) setPlaying(false);
    if (side) setGesture({ kind: 'scale', x0: e.clientX, items, range, side, k: 1, anchorT: side === 'right' ? range.min : range.max, edge: side === 'right' ? range.max : range.min, moved: false });
    else setGesture({ kind: 'keys', x0: e.clientX, items, mode: 'all', anchor: 0, part: 'range', delta: 0, atGo: false, moved: false, clickSel: null });
  };

  // ── cue 編號 / 轉場條 / 長度 ──
  const timedCues = useMemo(() => masters.filter(c => c.videoTimeSec != null).map(c => ({ id: c.id, time: c.videoTimeSec as number })), [masters]);
  // 拖 cue 編號：被夾住的 cue 轉場一起算（預覽和放開後同一個算法）
  const carryCues = useMemo(() => masters.filter(c => c.videoTimeSec != null).map(c => ({ id: c.id, time: c.videoTimeSec as number, crossfadeSeconds: c.crossfadeSeconds })), [masters]);
  const hasStart = !!tl?.start;
  const cueCanEdit = !!edit?.cue?.canEdit;
  const cueDraggable = (id: string) => cueCanEdit && !provisionalIds.has(id) && id !== LIB_START_ID && timedCues.some(c => c.id === id);
  const pressCue = (e: React.PointerEvent, id: string) => {
    e.stopPropagation();
    suppressCueClick.current = false;
    if (!cueDraggable(id) || e.button > 0 || gestureRef.current?.pending) return;
    focusSelf();
    capture(e);
    edit?.onGestureStart?.();
    const multi = e.shiftKey || e.ctrlKey || e.metaKey;
    const ids = cuePicked.has(id)
      ? new Set([...cuePicked].filter(c => cueDraggable(c)))
      : multi ? new Set([...cuePicked, id]) : new Set([id]);
    setGesture({ kind: 'cue', x0: e.clientX, cueId: id, ids, delta: 0, moved: false, pending: false, tl });
  };
  const clickCue = (e: React.MouseEvent, id: string) => {
    if (suppressCueClick.current) { suppressCueClick.current = false; return; }
    if ((e.shiftKey || e.ctrlKey || e.metaKey) && cueDraggable(id)) {
      setCuePicked(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
      return;
    }
    if (cuePicked.size) setCuePicked(EMPTY_SEL);
    if (playing) setPlaying(false);
    onSelectCue(id);
  };
  const pressTransition = (e: React.PointerEvent, w: { cueId: string; start: number; arrive: number }) => {
    e.stopPropagation();
    if (e.button > 0 || gestureRef.current?.pending) return;
    focusSelf();
    capture(e);
    edit?.onGestureStart?.();
    setGesture({
      kind: 'transition', x0: e.clientX, cueId: w.cueId, arrive: w.arrive, go0: w.start, prevArrive: prevArriveOf(visibleWindows, w.cueId),
      oldSec: w.arrive - w.start, seconds: w.arrive - w.start, moved: false, pending: false, tl,
    });
  };
  const lenNow = (edit?.length ? edit.length.seconds : lengthSec) ?? baseTotal;
  const pressLength = (e: React.PointerEvent) => {
    e.stopPropagation();
    if (e.button > 0 || gestureRef.current?.pending) return;
    focusSelf();
    capture(e);
    edit?.onGestureStart?.();
    lastPtr.current = { x: e.clientX, y: e.clientY, alt: e.altKey };
    setGesture({ kind: 'length', x0: e.clientX, len0: lenNow, total0: baseTotal, seconds: lenNow, moved: false, pending: false });
  };

  // ── 框選 ──
  const contentXY = (cx: number, cy: number) => {
    const r = contentRef.current?.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(width, cx - (r?.left ?? 0))),
      y: Math.max(0, Math.min(rows.length * ROW_H, cy - (r?.top ?? 0))),
    };
  };
  const pressEmpty = (e: React.PointerEvent) => {
    if (!edit || e.button > 0 || e.pointerType === 'touch') return;
    // 按在左右捲軸上不算
    const el = scrollRef.current;
    if (el && e.clientY - el.getBoundingClientRect().top > el.clientHeight) return;
    focusSelf();
    capture(e);
    if (playing) setPlaying(false);
    const p = contentXY(e.clientX, e.clientY);
    const how: MarqueeHow = e.ctrlKey || e.metaKey ? 'remove' : e.shiftKey ? 'add' : 'replace';
    lastPtr.current = { x: e.clientX, y: e.clientY, alt: e.altKey };
    setGesture({ kind: 'marquee', cx0: e.clientX, cy0: e.clientY, ax: p.x, ay: p.y, x: p.x, y: p.y, how, moved: false });
  };
  const marqueeHits = (g: Extract<Gesture, { kind: 'marquee' }>) =>
    keysInBox(placed, { x0: g.ax, y0: g.ay, x1: g.x, y1: g.y }, { pad: PAD, pps: scale, rowH: ROW_H }).map(keyId);

  // ── 拖曳共用：移動 / 放開 / 取消（每個可以拖的東西都掛這一組）──
  /** 用絕對位置算的（框選、長度）：指標沒動但自動捲動了也要重算 */
  const applyAbsolute = (cx: number, cy: number) => {
    const g = gestureRef.current;
    if (!g || g.pending) return;
    if (g.kind === 'marquee') {
      const p = contentXY(cx, cy);
      const moved = g.moved || Math.abs(cx - g.cx0) >= MOVE_THRESHOLD || Math.abs(cy - g.cy0) >= MOVE_THRESHOLD;
      if (moved) setGesture({ ...g, x: p.x, y: p.y, moved });
    } else if (g.kind === 'length') {
      const moved = g.moved || Math.abs(cx - g.x0) >= MOVE_THRESHOLD;
      if (!moved) return;
      const r = contentRef.current?.getBoundingClientRect();
      setGesture({ ...g, moved, seconds: lengthFromDrag((cx - (r?.left ?? 0) - PAD) / scale) });
    }
  };
  const onGestureMove = (e: React.PointerEvent) => {
    const g = gestureRef.current;
    if (!g || g.pending) return;
    if (g.kind === 'keys' && g.wait) { if (!(e.buttons & 1)) setGesture(null); return; }   // 等切 cue：還沒開始拖；手已經放開了就取消
    lastPtr.current = { x: e.clientX, y: e.clientY, alt: e.altKey };
    if (g.kind === 'marquee' || g.kind === 'length') { applyAbsolute(e.clientX, e.clientY); return; }
    const dx = e.clientX - g.x0;
    if (!g.moved && Math.abs(dx) < MOVE_THRESHOLD) return;
    const ds = dx / scale;
    if (g.kind === 'keys') {
      const r = dragShift(g.items, g.mode, g.anchor, ds, e.altKey);
      setGesture({ ...g, moved: true, delta: r.delta, atGo: r.stoppedAtGo });
    } else if (g.kind === 'scale') {
      const r = dragScale(g.items, g.range, g.side, ds, e.altKey);
      setGesture({ ...g, moved: true, k: r.k, anchorT: r.anchor, edge: r.edge });
    } else if (g.kind === 'cue') {
      setGesture({ ...g, moved: true, delta: cueMoveResult(timedCues, g.ids, g.cueId, ds, e.altKey).delta });
    } else if (g.kind === 'transition') {
      setGesture({ ...g, moved: true, seconds: transitionFromDrag(g.arrive, g.go0, g.prevArrive, ds, e.altKey) });
    }
  };
  /** 交給頁面（整批一步復原）；舊路徑一段一段存 */
  const commitTimings = (items: BatchMove[], next: TimingMap) => {
    const changes = changesFrom(items, next);
    if (!changes.length) return;
    if (edit) { edit.onTimings(changes.map(({ cueId: _cue, ...c }) => c)); return; }
    for (const { cueId, mechId, delaySec, durationSec } of changes) {
      void mech.commitTiming?.(cueId, mechId, { ...(delaySec !== undefined ? { delaySec } : {}), ...(durationSec !== undefined ? { durationSec } : {}) });
    }
  };
  /** cue 層級放開就存：等後台回來才放掉預覽（沒存成 → 回到原樣，存成 → 頁面給新資料） */
  const settle = (p: Promise<boolean>) => {
    const done = () => { if (gestureRef.current?.pending) setGesture(null); };
    p.then(done, done);
  };
  const onGestureUp = () => {
    const g = gestureRef.current;
    if (!g || g.pending) return;
    if (g.kind === 'keys' && g.wait) { setGesture(null); return; }   // 還沒切好就放開：不算拖（選取已經照按下的時候做了）
    if (g.kind === 'keys') {
      setGesture(null);
      if (!g.moved) { if (g.clickSel) applySel(g.clickSel, !!g.clickReplace); return; }
      commitTimings(g.items, shiftBatch(g.items, g.delta, g.mode));
    } else if (g.kind === 'scale') {
      setGesture(null);
      if (g.moved) commitTimings(g.items, scaleBatch(g.items, g.anchorT, g.k));
    } else if (g.kind === 'marquee') {
      setGesture(null);
      if (!g.moved) { if (g.how === 'replace') applySel(EMPTY_SEL, true); return; }
      applySel(combineSelection(selRef.current, marqueeHits(g), g.how), g.how === 'replace');
    } else if (g.kind === 'cue') {
      if (!g.moved) { setGesture(null); return; }   // 點一下：交給 onClick
      suppressCueClick.current = true;
      if (g.ids.size > 1) setCuePicked(g.ids); else if (cuePicked.size) setCuePicked(EMPTY_SEL);
      if (!edit?.cue || g.delta === 0) { setGesture(null); return; }
      const changes = carryCueMove(carryCues, g.ids, g.delta, hasStart).changes;
      setGesture({ ...g, pending: true });
      settle(edit.cue.onMoveCues(changes));
    } else if (g.kind === 'transition') {
      if (!edit?.cue || !g.moved || Math.abs(g.seconds - g.oldSec) < 1e-6) { setGesture(null); return; }
      setGesture({ ...g, pending: true });
      const own = g.cueId === edit.editableCueId ? moves.filter(m => m.cueId === g.cueId) : [];
      settle(edit.cue.onTransition(g.cueId, g.seconds, transitionScale(own, g.oldSec, g.seconds)));
    } else if (g.kind === 'length') {
      if (!edit?.length || !g.moved || g.seconds === g.len0) { setGesture(null); return; }
      setGesture({ ...g, pending: true });
      settle(edit.length.onSet(g.seconds));
    }
  };
  const onGestureCancel = () => { if (!gestureRef.current?.pending) setGesture(null); };
  const dragProps = { onPointerMove: onGestureMove, onPointerUp: onGestureUp, onPointerCancel: onGestureCancel };

  // 框選、拖長度時指標靠近邊緣 → 每幀一直捲，選取用資料座標算，捲出畫面的 key 也會記住
  const autoScrollKind = gesture && !gesture.pending && (gesture.kind === 'marquee' || gesture.kind === 'length') ? gesture.kind : null;
  useEffect(() => {
    if (!autoScrollKind) return;
    let raf = 0;
    const step = () => {
      const p = lastPtr.current, body = bodyRef.current, sc = scrollRef.current;
      if (p && body && sc) {
        const br = body.getBoundingClientRect(), sr = sc.getBoundingClientRect();
        const top = rulerWrapRef.current?.getBoundingClientRect().bottom ?? br.top;
        const vy = autoScrollKind === 'marquee' ? edgeScrollSpeed(p.y, top, br.bottom) : 0;
        const vx = edgeScrollSpeed(p.x, sr.left, sr.right);
        if (vy) body.scrollTop += vy;
        if (vx) sc.scrollLeft += vx;
        if (vx || vy) applyAbsolute(p.x, p.y);
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [autoScrollKind, scale, rows.length, width]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 鍵盤 ──
  const onBarKey = (e: React.KeyboardEvent, mv: TimelineMove) => {
    if (viewOnly || !cueEditable(mv.cueId)) return;
    const step = e.shiftKey ? 1 : 0.1;
    const cur = nudgeRef.current && nudgeRef.current.cueId === mv.cueId && nudgeRef.current.mechId === mv.mechId ? nudgeRef.current : null;
    const base: Nudge = cur ?? { cueId: mv.cueId, mechId: mv.mechId, delay0: mv.delay, dur0: mv.duration, delay: mv.delay, dur: mv.duration };
    let next: Nudge | null = null;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') next = { ...base, delay: clamp0(snapTime(base.delay + (e.key === 'ArrowRight' ? step : -step), false)) };
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') next = { ...base, dur: clamp0(snapTime(base.dur + (e.key === 'ArrowUp' ? step : -step), false)) };
    if (!next) return;
    e.preventDefault();
    setNudge(next);
  };
  const onBarKeyUp = (e: React.KeyboardEvent) => {
    if (!e.key.startsWith('Arrow')) return;
    const n = nudgeRef.current;
    setNudge(null);
    if (!n) return;
    const patch: { delaySec?: number; durationSec?: number } = {};
    if (n.delay !== n.delay0) patch.delaySec = n.delay;
    if (n.dur !== n.dur0) patch.durationSec = n.dur;
    if (!Object.keys(patch).length) return;
    if (edit) edit.onTimings([{ mechId: n.mechId, ...patch }]);
    else void mech.commitTiming?.(n.cueId, n.mechId, patch);
  };
  /** Esc：先取消正在拖的（框選、拖曳），沒有在拖才取消選取；Ctrl+A：全選（整條時間軸，別的 cue 的也算） */
  const onSectionKey = (e: React.KeyboardEvent) => {
    let handled = false;
    if (e.key === 'Escape') {
      const g = gestureRef.current;
      if (g && !g.pending) { if (g.kind === 'cue') suppressCueClick.current = true; setGesture(null); handled = true; }
      else if (edit && (selRef.current.size > 0 || cuePicked.size > 0)) {
        if (selRef.current.size > 0) applySel(EMPTY_SEL, true);
        setCuePicked(EMPTY_SEL);
        handled = true;
      }
    } else if (edit && (e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
      applySel(new Set(placed.map(keyId)), true);
      handled = true;
    }
    if (handled) { e.preventDefault(); e.stopPropagation(); }
  };

  // ── 預覽（拖到一半畫面照這個）──
  const live = gesture;
  const timingPreview: TimingMap | null = (() => {
    if (nudge) return new Map([[moveId(nudge.cueId, nudge.mechId), { delay: nudge.delay, duration: nudge.dur }]]);
    if (live?.kind === 'keys' && live.moved) return shiftBatch(live.items, live.delta, live.mode);
    if (live?.kind === 'scale' && live.moved) return scaleBatch(live.items, live.anchorT, live.k);
    return null;
  })();
  // 存回來之後 timeline 換了 → 不要再疊一次預覽
  const cueG = live?.kind === 'cue' && live.moved && !(live.pending && live.tl !== tl) ? live : null;
  const transG = live?.kind === 'transition' && live.moved && !(live.pending && live.tl !== tl) ? live : null;
  const carry = cueG ? carryCueMove(carryCues, cueG.ids, cueG.delta, hasStart) : null;
  /** 拖 cue 編號時每個 cue 的 GO 往哪走（被夾住的跟著走，越過別的 cue 後又被新鄰居夾住也算進去） */
  const cueShift = (id: string) => carry?.shifts.get(id)?.start ?? 0;
  const viewOf = (mv: TimelineMove) => {
    let go = mv.go + cueShift(mv.cueId), delay = mv.delay, duration = mv.duration, touched = false;
    // 拖 cue 編號：跟著 cue 的走幾秒 = 到位 − GO，兩邊移動的量不一樣（被夾住）時走的秒數也跟著變，放開後才不會跳
    const csh = carry?.shifts.get(mv.cueId);
    if (csh && !mv.explicitDuration) duration = Math.max(0, mv.duration + csh.arrive - csh.start);
    const p = timingPreview?.get(moveId(mv.cueId, mv.mechId));
    if (p) { delay = p.delay; duration = p.duration; touched = true; }
    if (transG && transG.cueId === mv.cueId) {
      const t = transitionMovePreview(mv, transG.seconds, transG.oldSec, mv.cueId === edit?.editableCueId);
      go = t.go; delay = t.delay; duration = t.duration;
    }
    const start = go + delay, end = start + duration;
    const state = touched ? null : cutStateOf(mv);
    const cutAt = state === 'cut' ? Math.max(start, Math.min(end, mv.cut!.at + cueShift(mv.cut!.byCueId))) : null;
    return { start, end, shownEnd: cutAt ?? end, duration, state, dragging: touched };
  };
  const liveWindow = (w: { cueId: string; start: number; arrive: number }) => {
    if (transG && transG.cueId === w.cueId) return { start: w.arrive - transG.seconds, arrive: w.arrive };
    const sh = carry?.shifts.get(w.cueId);
    return { start: w.start + (sh?.start ?? 0), arrive: w.arrive + (sh?.arrive ?? 0) };
  };
  const shownSel: ReadonlySet<string> = live?.kind === 'marquee' && live.moved
    ? combineSelection(sel, marqueeHits(live), live.how)
    : sel;

  // 範圍條：選到（而且拖得動）的 key 不在同一時間才有；拖的時候跟著預覽
  const rangeItems = edit ? batchOf(moves, sel, edit.editableCueId) : [];
  const liveRange = selectionRange(timingPreview
    ? rangeItems.map(i => { const p = timingPreview.get(moveId(i.cueId, i.mechId)); return p ? { ...i, delay: p.delay, duration: p.duration } : i; })
    : rangeItems);

  // 時間尺的刻度
  const tickStep = [1, 2, 5, 10, 15, 30, 60, 120].find(s => s * scale >= 56) ?? 300;
  const ticks: number[] = [];
  for (let t = 0; t <= total; t += tickStep) ticks.push(t);

  const hintText = viewOnly
    ? (readOnlyHint === null ? null : readOnlyHint ?? (timeClock ? STAGE_TIMELINE_TEXT.readOnlyHintVideo : STAGE_TIMELINE_TEXT.readOnlyHint))
    : edit
      ? `${single ? '0 秒 = 開始轉場。' : ''}點 key 選取、在空白處拖曳框選；拖 key 或色塊改時間（按住 Alt 不吸附）`
      : single ? '0 秒 = 開始轉場。拖整條改延遲、拖右邊改走幾秒（按住 Alt 不吸附）' : '拖整條改延遲、拖右邊改走幾秒（按住 Alt 不吸附）';
  const showStartLabel = !!startLabel && !!tl?.start;
  const cueReason = edit?.cue && !edit.cue.canEdit ? edit.cue.reason : null;

  // 可以改的時候（導演調整舞台）：標題列只留 cue 名稱、起始位置、播放、縮放；操作說明收進「?」和滑鼠提示
  const header = (
    <div className={'mech-tl__bar' + (edit ? ' mech-tl__bar--clean' : '')}>
      {edit ? (
        <>
          <strong className="mech-tl__title mech-tl__title--name" title={single ? `cue ${single.label} ${single.name}` : undefined}>
            {single ? <><span className="mech-tl__titleno">{single.label}</span>{single.name}</> : '機關時間軸'}
          </strong>
          {startControl ?? (showStartLabel && <span className="mech-tl__startlabel small mech-tl__startlabel--short" title={`第一個 cue 從這裡走過去：${startLabel}`}>起始：{startLabel}</span>)}
          {cueReason && <span className="mech-tl__lock" title={cueReason} aria-label={cueReason} role="img"><Icon icon={Lock} size="xs" /></span>}
          {hintText !== null && (
            <button type="button" className="mech-icon-btn" title={hintText} aria-label="時間軸怎麼操作" onClick={() => toast.info(hintText)}><Icon icon={CircleHelp} size="xs" /></button>
          )}
        </>
      ) : (
        <>
          <strong className="mech-tl__title">{single ? `Cue ${single.label} 的機關時間軸` : '機關時間軸'}</strong>
          {showStartLabel && <span className="mech-tl__startlabel small" title={`第一個 cue 從這裡走過去：${startLabel}`}>起始：{startLabel}</span>}
          {viewOnly && onAdjust ? (
            <button type="button" className="mech-tl__adjustbtn" onClick={onAdjust}
              title="只能看。要改機關的時間，切到「調整舞台」（跟上方的切換鈕一樣）">
              <Icon icon={SlidersHorizontal} size="xs" /> 調整舞台
            </button>
          ) : hintText !== null && (
            <span className="muted small mech-tl__hint" title={single ? '0 秒 = 這個 cue 開始轉場；預覽從預設位置開始動，在歌裡會從前一個 cue 的位置開始' : hintText}>
              {hintText}
            </span>
          )}
        </>
      )}
      <span className="grow" />
      {edit?.length && (
        <span className="muted small mech-tl__length" title={`${LENGTH_TEXT.source(edit.length.source)}。${LENGTH_TEXT.hint}`}>{formatLength(lenG ? lenG.seconds : lenNow)}</span>
      )}
      <span ref={clockRef} className="mech-tl__clock" aria-live="off" />
      <button type="button" className="mech-icon-btn" disabled={!tl}
        onClick={togglePlay}
        title={timeClock ? (timeClock.playing ? STAGE_TIMELINE_TEXT.pauseVideoTitle : STAGE_TIMELINE_TEXT.playVideoTitle) : (playing ? '暫停' : '播放（3D 畫面的機關跟著時間軸動）')}
        aria-label={(timeClock ? timeClock.playing : playing) ? '暫停' : '播放'}><Icon icon={(timeClock ? timeClock.playing : playing) ? Pause : Play} /></button>
      {!timeClock && (!edit || previewing) && (
        <button type="button" className="mech-icon-btn" disabled={!previewing} onClick={() => { setPlaying(false); setTime(null); }}
          title="停止預覽（機關回到目前選的 cue）" aria-label="停止預覽"><Icon icon={Square} /></button>
      )}
      {edit && <span className="mech-tl__barsep" aria-hidden="true" />}
      <button type="button" className="mech-icon-btn" onClick={() => setPps(p => Math.max(MIN_PPS, (p ?? 10) * 0.8))} title="縮小" aria-label="縮小"><Icon icon={ZoomOut} /></button>
      <button type="button" className="mech-icon-btn" onClick={() => setPps(p => Math.min(MAX_PPS, (p ?? 10) * 1.25))} title="放大" aria-label="放大"><Icon icon={ZoomIn} /></button>
      <button type="button" className="mech-icon-btn" onClick={fit} title="整首放進來" aria-label="整首放進來"><Icon icon={Maximize2} /></button>
      <button type="button" className="mech-icon-btn" onClick={onClose} title="收起機關時間軸" aria-label="收起機關時間軸"><Icon icon={ChevronDown} /></button>
    </div>
  );

  if (!showWhenEmpty && (!tl || mechanisms.length === 0)) {
    return (
      <section className={'mech-tl mech-tl--empty' + (viewOnly ? ' mech-tl--readonly' : '')} aria-label="機關時間軸">
        {header}
        <div className="mech-tl__empty muted small">
          {mechanisms.length === 0
            ? (viewOnly ? STAGE_TIMELINE_TEXT.noMechanisms : '還沒有機關：先在右邊「機關」分頁新增機關')
            : single ? '這個 cue 還沒有可以畫的機關'
            : viewOnly ? STAGE_TIMELINE_TEXT.noCueTimes
            : '這首歌還沒有正式的 cue：新增 cue 之後機關就會排在時間軸上'}
        </div>
      </section>
    );
  }

  if (!tl) return null;
  const cueLabel = (id: string) => (id === LIB_START_ID ? '預設位置' : `Cue ${cueNo.get(id) ?? '?'}「${cueById.get(id)?.name ?? ''}」`);
  const cueShort = (id: string) => (id === LIB_START_ID ? '預設位置' : `Cue ${cueNo.get(id) ?? '?'}`);
  const toggleGroup = (id: string) => setCollapsed(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const pressLabel = (e: React.MouseEvent, id: string) => {
    if (!edit) { mech.setFocusedId(id); return; }
    const how = e.ctrlKey || e.metaKey ? 'toggle' : e.shiftKey ? 'range' : 'replace';
    edit.onPick([id], how, pickOrder);
  };
  const rowActive = (id: string) => (edit ? picked.has(id) : mech.focusedId === id);
  const legacySel = (mv: TimelineMove) => mv.cueId === selectedCueId && mech.focusedId === mv.mechId;
  const gridlines = visibleWindows.map(w => <span key={w.cueId} className="mech-tl__gridline" style={{ left: X(liveWindow(w).arrive) }} />);
  const barHint = (mv: TimelineMove) => {
    if (viewOnly) return STAGE_TIMELINE_TEXT.barTip;
    if (legacy) return '拖整條改延遲、拖右邊改走幾秒';
    return cueEditable(mv.cueId) ? '拖曳改時間；Shift 加選、Ctrl 加減' : grabbable(mv.cueId) ? '按住直接拖：會先切到這個 cue' : '點一下選取';
  };

  /** 一個 key（開始 / 結束）：可以改時是按鈕，只能看時只是記號 */
  const keyEl = (mv: TimelineMove, end: KeyEnd, t: number, label: string, view: ReturnType<typeof viewOf>) => {
    const id = keyId({ cueId: mv.cueId, mechId: mv.mechId, end });
    const selected = viewOnly ? false : edit ? shownSel.has(id) : legacySel(mv);
    const draggable = grabbable(mv.cueId);
    const cls = 'mech-tl__key mech-tl__key--' + end
      + (selected ? ' is-selected' : '')
      + (end === 'end' && view.state === 'cut' ? ' is-cut' : '')
      + (view.state === 'cancelled' ? ' is-cancelled' : '')
      + (draggable ? ' is-draggable' : '')
      + (draggable && end === 'end' ? ' mech-tl__handle' : '');
    const title = `${end === 'begin' ? '開始' : '結束'}：${sheetTime(t)}\n${label}\n${barHint(mv)}`;
    if (viewOnly) return <span key={end} className={cls + ' is-view'} style={{ left: X(t) }} title={title} onClick={() => select(mv)} />;
    return (
      <button key={end} type="button" tabIndex={-1} className={cls} style={{ left: X(t) }} title={title}
        aria-label={`${end === 'begin' ? '開始' : '結束'} key：${label}`} aria-pressed={selected}
        onPointerDown={e => pressKeys(e, [id], mv, end)} {...dragProps} />
    );
  };

  /** 一排的標題行：每個 cue 一條合併的色塊（最早開始動 → 最後一個到位）；收起來時看這一條就知道整排什麼時候動 */
  const groupRow = (row: Extract<TlRow, { kind: 'group' }>) => {
    const byCue = new Map<string, { start: number; end: number; moves: TimelineMove[] }>();
    for (const m of row.members) {
      for (const mv of movesBy.get(m.id) || []) {
        const v = viewOf(mv);
        const cur = byCue.get(mv.cueId);
        if (!cur) byCue.set(mv.cueId, { start: v.start, end: v.shownEnd, moves: [mv] });
        else { cur.start = Math.min(cur.start, v.start); cur.end = Math.max(cur.end, v.shownEnd); cur.moves.push(mv); }
      }
    }
    const origin = row.collapsed && tl!.start ? row.members.some(m => tl!.start?.[m.id] !== undefined) : false;
    return (
      <div key={row.key} className={'mech-tl__row mech-tl__row--group' + (row.members.some(m => rowActive(m.id)) ? ' is-focused' : '')}>
        {gridlines}
        {origin && <span className="mech-tl__key mech-tl__key--origin" style={{ left: X(0) }} title={`起始位置${startLabel ? `（${startLabel}）` : ''}`} />}
        {[...byCue.entries()].map(([cueId, b]) => {
          const ids = b.moves.flatMap(mv => keyIdsOfMove(mv.cueId, mv.mechId));
          const beginIds = ids.filter((_, i) => i % 2 === 0), endIds = ids.filter((_, i) => i % 2 === 1);
          const beginSel = !!edit && beginIds.every(i => shownSel.has(i));
          const endSel = !!edit && endIds.every(i => shownSel.has(i));
          const sel = edit ? beginSel && endSel : cueId === selectedCueId && row.members.some(m => m.id === mech.focusedId);
          const label = `「${row.group.name}」${cueLabel(cueId)}：${b.moves.length} 個機關，${sheetTime(b.start)} 最早開始動、${sheetTime(b.end)} 最後一個到位`;
          const movable = !!edit && grabbable(cueId);
          return (
            <span key={cueId}>
              <button type="button" className={'mech-tl__gmove' + (sel ? ' is-selected' : '') + (movable ? ' is-draggable' : '')}
                style={{ left: X(b.start), width: Math.max(4, (b.end - b.start) * scale) }}
                title={viewOnly ? label : legacy ? `${label}\n展開這一排可以拖每一個機關的色塊` : `${label}\n點一下選整排這個 cue 的 key；按住可以直接拖`}
                aria-label={label}
                {...(edit ? { onPointerDown: (e: React.PointerEvent) => pressKeys(e, ids, b.moves[0], 'band'), ...dragProps } : { onPointerDown: (e: React.PointerEvent) => e.stopPropagation(), onClick: () => select(b.moves[0]) })}>
                {showMoveNo && (b.end - b.start) * scale > 46 && <span className="mech-tl__movelabel">{cueNo.get(cueId)}</span>}
              </button>
              <span className={'mech-tl__key mech-tl__key--begin is-passive' + (beginSel ? ' is-selected' : '')} style={{ left: X(b.start) }} aria-hidden="true" />
              <span className={'mech-tl__key mech-tl__key--end is-passive' + (endSel ? ' is-selected' : '')} style={{ left: X(b.end) }} aria-hidden="true" />
            </span>
          );
        })}
      </div>
    );
  };

  // 每排色塊上的 cue 編號：調整舞台時總覽列已經有編號和對齊線，不用每排重複（只能看的畫面照舊）
  const showMoveNo = !edit && !single;

  const summary = summaryKeys(moves.flatMap(mv => {
    const v = viewOf(mv);
    const [b, e] = keyIdsOfMove(mv.cueId, mv.mechId);
    return [{ time: v.start, selected: shownSel.has(b) }, { time: v.shownEnd, selected: shownSel.has(e) }];
  }), scale);

  // 拖的時候顯示的字
  const dragTip = (() => {
    if (nudge) return nudge.dur !== nudge.dur0 && nudge.delay === nudge.delay0 ? `走 ${formatSeconds(nudge.dur)} 秒` : `延遲 ${formatSeconds(nudge.delay)} 秒`;
    if (!live?.moved) return null;
    const limit = live.kind === 'keys' && live.atGo ? `。${TIMELINE_TEXT.goLimit}` : '';
    if (live.kind === 'keys') {
      if (live.part === 'range' && liveRange) return `開始 ${sheetTime(liveRange.min)}、結束 ${sheetTime(liveRange.max)}${limit}`;
      const one = live.items.length === 1 ? live.items[0] : null;
      if (one && live.mode === 'ends') return `走 ${formatSeconds(one.duration + live.delta)} 秒`;
      if (one && one.begin) return `延遲 ${formatSeconds(one.delay + live.delta)} 秒${limit}`;
      if (one) return `走 ${formatSeconds(one.duration + live.delta)} 秒`;
      return `一起移動 ${live.delta > 0 ? '+' : ''}${formatSeconds(live.delta)} 秒${limit}`;
    }
    if (live.kind === 'scale' && liveRange) return `開始 ${sheetTime(liveRange.min)}、結束 ${sheetTime(liveRange.max)}、共 ${formatSeconds(liveRange.max - liveRange.min)} 秒`;
    if (live.kind === 'cue') {
      const w = visibleWindows.find(x => x.cueId === live.cueId);
      return w ? sheetTime(w.arrive + live.delta) : null;
    }
    if (live.kind === 'transition') return `轉場 ${live.seconds.toFixed(1)} 秒`;
    if (live.kind === 'length') return `長度 ${formatLength(live.seconds)}`;
    return null;
  })();
  // 重疊的長句：平常用畫的（半實心、淡色、虛線）表現，滑鼠提示有全文；選到那一段才寫出來（觸控也看得到）
  const selectedOverlap = (() => {
    if (!edit || dragTip || sel.size === 0) return null;
    const hit = moves.filter(mv => { const [b, e] = keyIdsOfMove(mv.cueId, mv.mechId); return sel.has(b) && sel.has(e); });
    if (hit.length !== 1 || sel.size !== 2) return null;
    const def = mechanisms.find(m => m.id === hit[0].mechId);
    return def ? overlapText(hit[0], def.kind, cueShort) : null;
  })();

  const rulerH = strip ? C_RULER_H : RULER_H, overviewH = strip ? C_OVERVIEW_H : OVERVIEW_H;
  const lanes = rulerH + (edit && !strip ? RANGE_H : 0) + overviewH;
  const lenX = X(lenG ? lenG.seconds : lenNow);
  const hasEndline = !!edit?.length || lengthSec != null;

  const rulerBlock = (
          <div className="mech-tl__rulerwrap" ref={rulerWrapRef}>
            <div className="mech-tl__lanes" style={{ width }}>
              <div className="mech-tl__ruler" style={{ height: rulerH }} onPointerDown={onRulerDown} onPointerMove={onRulerMove} title={timeClock ? STAGE_TIMELINE_TEXT.rulerTipVideo : '點一下 / 拖：3D 畫面的機關跟著這個時間'}>
                {ticks.map(t => (
                  <span key={t} className="mech-tl__tick" style={{ left: X(t) }}>{sheetTime(t).replace(/\.0$/, '')}</span>
                ))}
              </div>
              {edit && !strip && (
                <div className="mech-tl__rangelane" style={{ height: RANGE_H }}>
                  {liveRange && (
                    <>
                      <button type="button" tabIndex={-1} className="mech-tl__range" style={{ left: X(liveRange.min), width: (liveRange.max - liveRange.min) * scale }}
                        title={`選到的 key：${sheetTime(liveRange.min)} 到 ${sheetTime(liveRange.max)}\n拖中間整批平移，拖兩端依比例縮放`} aria-label="拖曳整批平移選到的 key"
                        onPointerDown={e => pressRange(e, null)} {...dragProps} />
                      <button type="button" tabIndex={-1} className="mech-tl__range-handle" style={{ left: X(liveRange.min) - 7 }} title="拖曳依比例縮放（右端固定）" aria-label="範圍條左端"
                        onPointerDown={e => pressRange(e, 'left')} {...dragProps} />
                      <button type="button" tabIndex={-1} className="mech-tl__range-handle" style={{ left: X(liveRange.max) }} title="拖曳依比例縮放（左端固定）" aria-label="範圍條右端"
                        onPointerDown={e => pressRange(e, 'right')} {...dragProps} />
                    </>
                  )}
                </div>
              )}
              <div className="mech-tl__overview" style={{ height: overviewH }} onPointerDown={onRulerDown} onPointerMove={onRulerMove}>
                {visibleWindows.length === 0 && !single && (
                  <span className="mech-tl__nocue muted small" title="在「Cue」分頁替 cue 設好「對應影片時間」，這裡就會出現 cue 編號">{untimed === 0 && masters.length === 0 ? '還沒有 cue' : '還沒有設時間的 cue'}</span>
                )}
                {summary.map(k => <span key={k.time} className={'mech-tl__mini' + (k.selected ? ' is-selected' : '')} style={{ left: X(k.time) }} aria-hidden="true" />)}
                {visibleWindows.map((w, i) => {
                  const prov = provisionalIds.has(w.cueId);
                  const cue = cueById.get(w.cueId);
                  const lw = liveWindow(w);
                  const actual = lw.arrive - lw.start;
                  // 舊模式的第一個 cue 本來就不走（只當起始值），不用畫設定的長度
                  const noMotion = i === 0 && !tl.start;
                  const setSec = cue && !noMotion ? cueTransitionSeconds(cue) : actual;
                  const tt = transitionText(setSec, actual);
                  const ghostStart = Math.max(0, lw.arrive - setSec);
                  const showGhost = tt.shortened && !transG && lw.start - ghostStart > 0.01;
                  const draggable = cueDraggable(w.cueId);
                  const dragging = cueG && cueG.ids.has(w.cueId);
                  const barX = X(lw.start), noX = X(lw.arrive);
                  const cueTitle = `${cueLabel(w.cueId)}：${sheetTime(lw.start)} GO、${sheetTime(lw.arrive)} 到位${tt.shortened ? `（${tt.text}）` : ''}${prov ? `（暫排）\n${STAGE_TIMELINE_TEXT.provisionalTip}` : ''}`
                    + (draggable ? '\n拖這個編號改時間；Shift / Ctrl 點可以多選' : '');
                  return (
                    <span key={w.cueId}>
                      {showGhost && <span className="mech-tl__go-ghost" style={{ left: X(ghostStart), width: (lw.start - ghostStart) * scale }} title={tt.text} />}
                      {actual > 0 && <span className={'mech-tl__go' + (prov ? ' is-provisional' : '')} style={{ left: barX, width: actual * scale }} title={tt.text} onPointerDown={e => e.stopPropagation()} onClick={() => onSelectCue(w.cueId)} />}
                      {draggable && (
                        <button type="button" tabIndex={-1} className="mech-tl__gogrip" style={{ left: Math.min(barX - 4, noX - 20) }}
                          title={`${tt.text}\n左右拖曳改轉場秒數（編號不動）`} aria-label={`${cueShort(w.cueId)} 轉場條左端：拖曳改轉場秒數`}
                          onPointerDown={e => pressTransition(e, w)} {...dragProps} />
                      )}
                      <button type="button"
                        className={'mech-tl__cue' + (w.cueId === selectedCueId ? ' is-selected' : '') + (prov ? ' is-provisional' : '') + (draggable ? ' is-draggable' : '') + (cuePicked.has(w.cueId) ? ' is-picked' : '') + (dragging ? ' is-dragging' : '')}
                        style={{ left: noX }} title={cueTitle} aria-label={cueTitle} aria-pressed={cuePicked.has(w.cueId) || undefined}
                        onPointerDown={e => pressCue(e, w.cueId)} onClick={e => clickCue(e, w.cueId)} {...(draggable ? dragProps : {})}>{cueNo.get(w.cueId)}</button>
                      {dragging && live?.kind === 'cue' && w.cueId === live.cueId && <span className="mech-tl__dragtip" style={{ left: noX }}>{sheetTime(lw.arrive)}</span>}
                      {transG && transG.cueId === w.cueId && <span className="mech-tl__dragtip" style={{ left: barX }}>轉場 {transG.seconds.toFixed(1)} 秒</span>}
                    </span>
                  );
                })}
              </div>
              {edit?.length?.editable && (
                <button type="button" tabIndex={-1} className="mech-tl__lenhandle" style={{ left: lenX - 4, height: lanes }}
                  title={`歌曲長度 ${formatLength(lenG ? lenG.seconds : lenNow)}\n左右拖曳改長度`} aria-label="拖曳改歌曲長度"
                  onPointerDown={pressLength} {...dragProps} />
              )}
              {lenG && lenG.moved && <span className="mech-tl__dragtip mech-tl__dragtip--length" style={{ left: lenX }}>{formatLength(lenG.seconds)}</span>}
              <div ref={rulerHeadRef} className="mech-tl__head" style={{ display: 'none' }} aria-hidden="true" />
            </div>
          </div>
  );

  if (strip) {
    const playingNow = timeClock ? timeClock.playing : playing;
    return (
      <section ref={sectionRef} tabIndex={-1} style={{ height: COLLAPSED_H }}
        className={'mech-tl mech-tl--collapsed' + (viewOnly ? ' mech-tl--readonly' : '')} aria-label="機關時間軸（已收起）">
        <div className="mech-tl__strip">
          <div className="mech-tl__stripside" style={{ width: LABEL_W }}>
            <div className="mech-tl__striprow">
              <button type="button" className="mech-icon-btn" disabled={!onOpen} onClick={() => onOpen?.()}
                title="展開機關時間軸" aria-label="展開機關時間軸"><Icon icon={ChevronUp} /></button>
              <strong className="mech-tl__title">時間軸</strong>
            </div>
            <div className="mech-tl__striprow">
              <button type="button" className="mech-icon-btn" onClick={togglePlay}
                title={timeClock ? (timeClock.playing ? STAGE_TIMELINE_TEXT.pauseVideoTitle : STAGE_TIMELINE_TEXT.playVideoTitle) : (playing ? '暫停' : '播放（3D 畫面的機關跟著時間軸動）')}
                aria-label={playingNow ? '暫停' : '播放'}><Icon icon={playingNow ? Pause : Play} /></button>
              {!timeClock && previewing && (
                <button type="button" className="mech-icon-btn" onClick={() => { setPlaying(false); setTime(null); }}
                  title="停止預覽（機關回到目前選的 cue）" aria-label="停止預覽"><Icon icon={Square} /></button>
              )}
              <span ref={clockRef} className="mech-tl__clock" aria-live="off" />
            </div>
          </div>
          <div className="mech-tl__main">{rulerBlock}</div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} tabIndex={-1} onKeyDown={onSectionKey} style={{ height }}
      className={'mech-tl' + (viewOnly ? ' mech-tl--readonly' : '') + (edit ? ' mech-tl--editing' : '')} aria-label="機關時間軸">
      <div className="mech-tl__resize" role="separator" aria-orientation="horizontal" aria-label="拖曳調整時間軸高度" title="上下拖曳調整高度（雙擊回到預設）" {...heightHandle} />
      {header}
      <div className="mech-tl__body" ref={bodyRef}>
        <div className="mech-tl__labels" style={{ width: LABEL_W }}>
          <div className="mech-tl__ruler-pad" style={{ height: lanes }}>{untimed > 0 ? <span className="muted small" title={STAGE_TIMELINE_TEXT.provisionalTip}>{untimed} {STAGE_TIMELINE_TEXT.provisionalCount}</span> : null}</div>
          {rows.length === 0 && !single && (
            <div className="mech-tl__norow muted small" title="先在右邊「機關」分頁新增機關，這裡才會畫出它們什麼時候動">還沒有機關</div>
          )}
          {rows.map(row => (row.kind === 'group' ? (
            <button key={row.key} type="button" className={'mech-tl__label mech-tl__label--group' + (row.members.some(m => rowActive(m.id)) ? ' is-focused' : '')}
              aria-expanded={!row.collapsed} onClick={() => toggleGroup(row.group.id)}
              title={`${row.group.name}（${row.members.length} 個機關）：${row.collapsed ? '按一下展開' : '按一下收起來'}`}>
              <Icon icon={row.collapsed ? ChevronRight : ChevronDown} size="sm" />{row.group.name}
            </button>
          ) : (
            <button key={row.key} type="button"
              className={'mech-tl__label' + (row.member ? ' mech-tl__label--member' : '') + (mech.focusedId === row.mech.id && !edit ? ' is-focused' : '') + (edit && picked.has(row.mech.id) ? ' is-picked' : '')}
              aria-pressed={edit ? picked.has(row.mech.id) : undefined}
              onClick={e => pressLabel(e, row.mech.id)} title={edit ? `${row.mech.name}（Ctrl 加減、Shift 連選）` : row.mech.name}>{row.mech.name}</button>
          )))}
        </div>
        <div className="mech-tl__main">
          {rulerBlock}
          <div className="mech-tl__scroll" ref={scrollRef} onScroll={syncRuler} {...(edit ? { onPointerDown: pressEmpty, ...dragProps } : {})}>
            <div className="mech-tl__content" ref={contentRef} style={{ width }}>
              {rows.map(row => {
                if (row.kind === 'group') return groupRow(row);
                const m = row.mech;
                const originV = tl.start?.[m.id];
                return (
                  <div key={m.id} className={'mech-tl__row' + (rowActive(m.id) ? ' is-focused' : '')}>
                    {gridlines}
                    {originV !== undefined && (
                      <span className="mech-tl__key mech-tl__key--origin" style={{ left: X(0) }}
                        title={`起始位置：${formatMechValue(originV, m.kind)}${startLabel ? `（${startLabel}）` : ''}`} />
                    )}
                    {(movesBy.get(m.id) || []).map(mv => {
                      const v = viewOf(mv);
                      const [bId, eId] = keyIdsOfMove(mv.cueId, mv.mechId);
                      const sel_ = viewOnly ? false : edit ? shownSel.has(bId) && shownSel.has(eId) : legacySel(mv);
                      const editable = grabbable(mv.cueId);
                      const overlap = overlapText(mv, m.kind, cueShort);
                      const label = `「${m.name}」${cueLabel(mv.cueId)}：${sheetTime(v.start)} 開始、走 ${formatSeconds(v.duration)} 秒、${sheetTime(v.end)} 到位（${formatMechValue(mv.from, m.kind)} → ${formatMechValue(mv.to, m.kind)}）${overlap ? `\n${overlap}` : ''}`;
                      return (
                        <span key={mv.cueId}>
                          <button
                            type="button"
                            className={'mech-tl__move' + (sel_ ? ' is-selected' : '') + (v.state === 'cut' ? ' is-cut' : '') + (v.state === 'cancelled' ? ' is-cancelled' : '') + (v.dragging ? ' is-dragging' : '') + (editable ? ' is-draggable' : '')}
                            style={{ left: X(v.start), width: Math.max(3, (v.shownEnd - v.start) * scale) }}
                            title={`${label}\n${barHint(mv)}`}
                            aria-label={label}
                            aria-pressed={edit ? sel_ : undefined}
                            {...editHandlers(!can.drag, {
                              onPointerDown: (e: React.PointerEvent) => pressKeys(e, [bId, eId], mv, 'band'),
                              ...dragProps,
                              onKeyDown: (e: React.KeyboardEvent) => onBarKey(e, mv),
                              onKeyUp: onBarKeyUp,
                            })}
                            onClick={viewOnly || legacy ? () => select(mv) : undefined}
                          >
                            {showMoveNo && (v.shownEnd - v.start) * scale > 46 && <span className="mech-tl__movelabel">{cueNo.get(mv.cueId)}</span>}
                          </button>
                          {keyEl(mv, 'begin', v.start, label, v)}
                          {keyEl(mv, 'end', v.shownEnd, label, v)}
                        </span>
                      );
                    })}
                  </div>
                );
              })}
              {hasEndline && <span className="mech-tl__endline" style={{ left: lenX }} aria-hidden="true" />}
              {live?.kind === 'marquee' && live.moved && (
                <div className="mech-tl__marquee" aria-hidden="true" style={{
                  left: Math.min(live.ax, live.x), top: Math.min(live.ay, live.y),
                  width: Math.abs(live.x - live.ax), height: Math.abs(live.y - live.ay),
                }} />
              )}
              <div ref={headRef} className="mech-tl__head" style={{ display: 'none' }} aria-hidden="true" />
            </div>
          </div>
          {/* 左右捲動條固定在最下面：上下捲到哪都看得到 */}
          <div className="mech-tl__hbar" ref={hbarRef} aria-hidden="true" onScroll={() => { if (scrollRef.current && hbarRef.current) scrollRef.current.scrollLeft = hbarRef.current.scrollLeft; }}>
            <div style={{ width, height: 1 }} />
          </div>
        </div>
      </div>
      {(dragTip || selectedOverlap) && <div className="mech-tl__drag muted small" role="status">{dragTip ?? selectedOverlap}</div>}
    </section>
  );
}
